import { useEffect } from 'react';
import { ArrowUpRight, Truck } from 'lucide-react';

/**
 * `/supplier-order-management` and `/som-product-master` — moved, and this is the part of the
 * move that runs in the browser.
 *
 * Supplier Order Management became an application of its own on 2026-09-24 (phase P7 of
 * `supplier-order-app/context/coordination-plans/open/2026-09-19-supplier-order-management-eigenes-repo.md`),
 * at `som.dolderapps.com`. The two pages keep their paths there, so the redirect is a host
 * swap: `/som-product-master` here lands on `/som-product-master` there.
 *
 * ## Why three redirects and not one (D12, the same shape as Customer 360's D10)
 *
 * 1. A **302** in `staticwebapp.config.json` catches a bookmark — a full page load that
 *    reaches the server.
 * 2. **This component** catches navigation inside the already-loaded app (the menu entry),
 *    which never reaches the server because React Router handles it in the browser.
 * 3. **`web/public/sw-som-cutover.js`** catches the pages that are already open on these
 *    paths behind a warm service worker, which would otherwise answer from its precache.
 *
 * ## The query string
 *
 * Neither page reads one, here or there (measured 2026-09-24: the state lives in React state
 * and sessionStorage). It is carried anyway, as `/customer-360` does, because this is the one
 * redirect that can: Static Web Apps drops the query string on a cross-origin 302 (measured on
 * the Customer 360 cutover, `context/docs/customer-360-cutover.md`).
 *
 * ## The page code is gone; this door is not
 *
 * The two pages and their components stood unrouted after P7 so the rollback of the door was
 * one commit. SOM P8 (2026-09-24) deleted them. This component, its two routes, the 302 in
 * `staticwebapp.config.json` and `sw-som-cutover.js` stay: they carry the bookmarks.
 */
const SOM_APP = 'https://som.dolderapps.com';

export function SupplierOrderManagementMoved({ path }: { path: '/supplier-order-management' | '/som-product-master' }) {
    const target = SOM_APP + path + window.location.search + window.location.hash;

    useEffect(() => {
        // replace(), not assign(): back should not land on a page that sends you forward again.
        window.location.replace(target);
    }, [target]);

    return (
        <div className="mx-auto max-w-2xl px-4 py-16">
            <div className="mb-4 flex items-center gap-2">
                <Truck className="h-6 w-6 text-blue-600" />
                <h1 className="text-xl font-semibold text-slate-800">
                    Supplier Order Management has moved
                </h1>
            </div>

            <p className="text-sm text-slate-600">
                It is an application of its own now, at{' '}
                <span className="font-medium text-slate-800">som.dolderapps.com</span>.
                Taking you there…
            </p>

            <a
                href={target}
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2
                           text-sm font-medium text-white transition-colors hover:bg-blue-500"
            >
                Open Supplier Order Management
                <ArrowUpRight className="h-4 w-4" />
            </a>

            <p className="mt-6 text-xs text-slate-400">
                Same proposals, same product master, same numbers. If it does not let you in, ask Roman.
            </p>
        </div>
    );
}
